'use client'

import { useEffect } from 'react'
import { useLocation } from 'react-router'
import { useAccountCart } from '@/components/account-cart-provider'
import type { AccountType } from '@/components/account-cart-provider'
import {
  accountTypeToAnalyticsProductItem,
  cartItemsToAnalyticsProducts,
} from '@/lib/analytics-product-context'
import { syncApplicationAnalyticsContext } from '@/lib/analytics'
import {
  pushAddToCart,
  pushBeginCheckout,
  pushRemoveFromCart,
  pushSelectItem,
} from '@/lib/gtm-data-layer'
import { captureUtmParamsFromWindow } from '@/lib/utm-params'
import { isBusinessAppPath } from '@/lib/business-app-path'

type CartPingDetail = {
  action?: 'add' | 'remove' | 'update' | 'clear'
  accountType?: AccountType
}

function resolveApplicationType(pathname: string): 'consumer' | 'business' | null {
  if (isBusinessAppPath(pathname)) {
    return 'business'
  }

  if (
    pathname === '/accounts'
    || pathname.startsWith('/apply')
    || pathname === '/'
  ) {
    return 'consumer'
  }

  return null
}

export default function GtmAnalytics() {
  const { cart } = useAccountCart()
  const { pathname, search } = useLocation()
  const applicationType = resolveApplicationType(pathname)

  useEffect(() => {
    captureUtmParamsFromWindow()
  }, [pathname, search])

  useEffect(() => {
    if (!applicationType) {
      return
    }

    syncApplicationAnalyticsContext({
      applicationType: applicationType === 'business' ? 'business' : 'consumer',
      cart,
    })
  }, [applicationType, cart])

  useEffect(() => {
    if (!applicationType) {
      return
    }

    const handleCartPing = (event: Event) => {
      const detail = (event as CustomEvent<CartPingDetail>).detail
      if (!detail?.accountType) {
        return
      }

      const product = accountTypeToAnalyticsProductItem(detail.accountType)

      if (detail.action === 'add' || detail.action === 'update') {
        pushAddToCart(product)
        pushSelectItem(product, 'select')
        return
      }

      if (detail.action === 'remove') {
        pushRemoveFromCart(product)
        pushSelectItem(product, 'deselect')
      }
    }

    window.addEventListener('cart-ping', handleCartPing)
    return () => window.removeEventListener('cart-ping', handleCartPing)
  }, [applicationType])

  return null
}

export function pushBeginCheckoutFromCart(
  cart: ReturnType<typeof useAccountCart>['cart'],
  applicationType: 'consumer' | 'business',
): void {
  const products = cartItemsToAnalyticsProducts(cart)
  syncApplicationAnalyticsContext({
    applicationType,
    cart,
  })

  pushBeginCheckout({
    application_type: applicationType,
    products,
  })
}
